/**
 * Domain Service - Serviço para gerenciamento de domínios permitidos
 *
 * Fornece métodos para buscar, criar, atualizar e deletar domínios
 */

import { api } from './api';
import {
  Domain,
  DomainBackend,
  DomainsListResponse,
  CreateDomainPayload,
  UpdateDomainPayload
} from '@/types/domain';

// Helper: Transforma resposta do backend para formato frontend
const transformDomain = (backendDomain: DomainBackend): Domain => ({
  id: backendDomain.id_user_domain,
  userId: backendDomain.id_user,
  baseDomain: backendDomain.base_domain,
  createdAt: backendDomain.created_at,
  updatedAt: backendDomain.updated_at,
});

export const domainService = {
  /**
   * GET /user-domain/:id_user
   * Lista todos os domínios de um usuário
   */
  getDomains: async (userId: string): Promise<Domain[]> => {
    try {
      const response = await api.get<DomainsListResponse>(`/user-domain/${userId}`);

      // Backend retorna 204 quando vazio
      if (!response || !response.data) {
        return [];
      }

      return response.data.map(transformDomain);
    } catch (error: any) {
      // 204 No Content é sucesso (lista vazia)
      if (error?.response?.status === 204) {
        return [];
      }
      throw error;
    }
  },

  /**
   * POST /user-domain/:id_user
   * Cria um novo domínio
   */
  createDomain: async (userId: string, payload: CreateDomainPayload): Promise<void> => {
    await api.post(`/user-domain/${userId}`, payload);
    // Backend retorna apenas { message }, não retorna o objeto criado
  },

  /**
   * PUT /user-domain/:id_user_domain
   * Atualiza um domínio existente
   */
  updateDomain: async (domainId: string, payload: UpdateDomainPayload): Promise<void> => {
    await api.put(`/user-domain/${domainId}`, payload);
    // Backend retorna apenas { message }
  },

  /**
   * DELETE /user-domain/:id_user_domain
   * Deleta um domínio
   */
  deleteDomain: async (domainId: string): Promise<void> => {
    await api.delete(`/user-domain/${domainId}`);
    // Backend retorna 204 No Content
  },
};

export default domainService;
